import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase/config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail
} from "firebase/auth";
import { ref, set, push, onValue } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { generateClinicalId } from "../utils/idGenerator";
import "./auth.css";

const AuthPage = () => {
  const { currentUser, userData, loading: authLoading } = useAuth();
  const { showAlert } = useToast();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgot, setIsForgot] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "patient",
    // Patient specific
    age: "",
    phone: "",
    // Doctor specific
    specialty: "General Physician",
    license: "",
    experience: "",
    hospitalName: "",
    hospitalId: ""
  });

  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Synchronize active facility network for auto-suggestion
    const hospitalsRef = ref(db, "hospitals");
    const unsubscribe = onValue(hospitalsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data)
          .filter(([id, val]) => val.status === "active")
          .map(([id, val]) => ({ id, ...val }));
        setHospitals(list);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      showAlert("PROTOCOL_ERROR: Email identity required for recovery sequence.", "error");
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, formData.email);
      showAlert("RECOVERY_LINK_TRANSMITTED: Clinical reset protocol active. Please check your inbox.", "success");
      setIsForgot(false);
    } catch (error) {
      showAlert("RECOVERY_FAILURE: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Auto-link hospital ID if name matches existing facility
    if (name === "hospitalName") {
      const match = hospitals.find(h => h.name.toLowerCase() === value.toLowerCase());
      setFormData(prev => ({ ...prev, hospitalId: match ? match.id : "" }));
    }
  };

  const handleRoleChange = (e) => {
    setFormData({ ...formData, role: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, formData.email, formData.password);
        showAlert("Login successful!", "success");
      } else {
        // Clinical Validation
        if (formData.role === "doctor") {
          if (!formData.license) {
            throw new Error("MANDATORY_FIELD_MISSING: Medical License required for doctor identity.");
          }
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
        const user = userCredential.user;
        
        // Conditional Identity Verification (Exempting Caregivers)
        if (formData.role !== "caregiver") {
          await sendEmailVerification(user);
        }

        // Provision Unique Clinical ID
        const customId = await generateClinicalId(formData.role);

        // Facility Onboarding Logic
        let finalHospitalId = formData.hospitalId;
        if (formData.role === "doctor" && !finalHospitalId && formData.hospitalName) {
          // Propose new facility node
          const hspId = await generateClinicalId("hospital");
          const newHospRef = ref(db, `hospitals/${hspId}`);
          await set(newHospRef, {
            id: hspId,
            name: formData.hospitalName,
            status: "pending",
            createdBy: user.uid,
            createdAt: new Date().toISOString()
          });
          finalHospitalId = hspId;
          showAlert("FACILITY_PROPOSED: Onboarding node saved as 'pending'. Admin review required.", "info");
        }

        const userProfile = {
          id: user.uid,
          uid: user.uid,
          customId: customId,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          status: "active",
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          emailVerified: false
        };

        if (formData.role === "doctor") {
          userProfile.specialty = formData.specialty;
          userProfile.license = formData.license;
          userProfile.experience = parseInt(formData.experience) || 0;
          userProfile.hospitalId = finalHospitalId || "INDEPENDENT_NODE";
          userProfile.status = "pending"; // Doctors need verification
        } else if (formData.role === "patient") {
          userProfile.phone = formData.phone;
          userProfile.age = parseInt(formData.age) || 0;
          userProfile.healthScore = 100;
        }

        const targetNode = formData.role === "caregiver" ? `caregivers/${user.uid}` : `users/${user.uid}`;
        await set(ref(db, targetNode), userProfile);
        
        const successMessage = formData.role === "caregiver" 
          ? `PROTOCOL_AUTHORIZED: Identity provisioned as ${customId}. Session active.`
          : `PROTOCOL_AUTHORIZED: Identity provisioned as ${customId}. Please verify your email.`;
          
        showAlert(successMessage, "success");
      }
    } catch (error) {
      showAlert("AUTHORIZATION_BREACH: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="AuthPage-container">
      <div className="AuthPage-grid">

        {/* Left Branding */}
        <div className="AuthPage-left">
          <div className="AuthPage-brand-header">
            <div className="AuthPage-brand-icon">
              <svg width="40" height="40" fill="#2563eb">
                <circle cx="20" cy="20" r="18" stroke="#2563eb" strokeWidth="3" fill="none" />
              </svg>
            </div>
            <h1 className="AuthPage-brand-title">Medico.in</h1>
          </div>

          <p className="AuthPage-brand-subtitle">
            Advanced IoT Head Health Monitoring System
          </p>

          <div className="AuthPage-feature-card">
            <h3>Real-Time Monitoring</h3>
            <p>Track temperature, pressure, and pain levels continuously.</p>
          </div>

          <div className="AuthPage-feature-card">
            <h3>Smart Analytics</h3>
            <p>AI-powered insights and historical data tracking.</p>
          </div>

          <div className="AuthPage-feature-card">
            <h3>Emergency Alerts</h3>
            <p>Instant notifications for critical conditions.</p>
          </div>
        </div>

        {/* Right Form */}
        <div className="AuthPage-right">
          <div className="AuthPage-form-card">
            <h2 className="AuthPage-form-title">Welcome</h2>
            <p className="AuthPage-form-subtitle">
              {isLogin ? "Sign in to access your dashboard" : "Create your account"}
            </p>

            <div className="AuthPage-tab-switcher">
              {!isForgot && (
                <>
                  <button
                    className={isLogin ? "active" : ""}
                    onClick={() => setIsLogin(true)}
                  >
                    Login
                  </button>
                  <button
                    className={!isLogin ? "active" : ""}
                    onClick={() => setIsLogin(false)}
                  >
                    Register
                  </button>
                </>
              )}
              {isForgot && (
                <button className="active" style={{ width: '100%' }}>
                  Recovery Mode
                </button>
              )}
            </div>

            <form onSubmit={isForgot ? handleForgotPassword : handleSubmit}>
              {isForgot && (
                <div className="AuthPage-recovery-protocol">
                  <p style={{ color: 'var(--admin-text-dim)', fontSize: '0.8rem', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                    &gt; SECURITY_OVERRIDE_ENABLED<br />
                    &gt; PLEASE_IDENTIFY_EMAIL_FOR_RESET_TRANSMISSION
                  </p>
                </div>
              )}

              {!isLogin && !isForgot && (
                <div className="AuthPage-form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}

              <div className="AuthPage-form-group">
                <label>Email ID</label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@clinical_node.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {!isForgot && (
                <div className="AuthPage-form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Access Code</label>
                    {isLogin && (
                      <span
                        onClick={() => setIsForgot(true)}
                        style={{ fontSize: '0.75rem', color: 'var(--admin-accent-blue)', cursor: 'pointer', fontWeight: 600 }}
                      >
                        RECOVER_PASSWORD?
                      </span>
                    )}
                  </div>
                  <input
                    type="password"
                    name="password"
                    placeholder="********"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}

              {!isLogin && !isForgot && (
                <div className="AuthPage-form-group">
                  <label>I am a</label>
                  <select name="role" value={formData.role} onChange={handleRoleChange}>
                    <option value="patient">Patient</option>
                    <option value="doctor">Doctor / Healthcare Provider</option>
                    <option value="caregiver">Caregiver / Family Member</option>
                    {/* <option value="admin">System Administrator</option> */}
                  </select>
                </div>
              )}

              {!isLogin && !isForgot && formData.role === "patient" && (
                <div className="AuthPage-clinical-sector animate-fade-in">
                  <div className="AuthPage-form-group">
                    <label>Age</label>
                    <input type="number" name="age" placeholder="25" value={formData.age} onChange={handleChange} required />
                  </div>
                  <div className="AuthPage-form-group">
                    <label>Phone Number</label>
                    <input type="text" name="phone" placeholder="+91 XXXX XXXX" value={formData.phone} onChange={handleChange} required />
                  </div>
                </div>
              )}

              {!isLogin && !isForgot && formData.role === "doctor" && (
                <div className="AuthPage-clinical-sector animate-fade-in">
                  <div className="AuthPage-form-group">
                    <label>Medical Specialty</label>
                    <select name="specialty" value={formData.specialty} onChange={handleChange}>
                      <option value="General Physician">General Physician</option>
                      <option value="Cardiologist">Cardiologist</option>
                      <option value="Neurologist">Neurologist</option>
                      <option value="Pediatrician">Pediatrician</option>
                      <option value="Surgeon">Surgeon</option>
                    </select>
                  </div>
                  <div className="AuthPage-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="AuthPage-form-group">
                      <label>Medical License *</label>
                      <input type="text" name="license" placeholder="MC-12345" value={formData.license} onChange={handleChange} required />
                    </div>
                    <div className="AuthPage-form-group">
                      <label>Exp (Years)</label>
                      <input type="number" name="experience" placeholder="5" value={formData.experience} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="AuthPage-form-group">
                    <label>Clinical Facility (Select or Enter New)</label>
                    <input
                      type="text"
                      name="hospitalName"
                      list="hospital-list"
                      placeholder="Hospital Name"
                      value={formData.hospitalName}
                      onChange={handleChange}
                      required
                    />
                    <datalist id="hospital-list">
                      {hospitals.map(h => <option key={h.id} value={h.name} />)}
                    </datalist>
                    {formData.hospitalId ? (
                      <span style={{ fontSize: '0.65rem', color: 'var(--accent-cyan)', fontFamily: 'Space Mono, monospace' }}>
                        NODE_IDENTIFIED: {formData.hospitalId}
                      </span>
                    ) : formData.hospitalName && (
                      <span style={{ fontSize: '0.65rem', color: 'var(--accent-orange)', fontFamily: 'Space Mono, monospace' }}>
                        NEW_FACILITY_PROPOSAL: Requires Admin Approval
                      </span>
                    )}
                  </div>
                </div>
              )}

              <button className="AuthPage-submit-btn" type="submit" disabled={loading}>
                {loading ? "TRANSMITTING..." : isForgot ? "SEND_RECOVERY_PROTOCOL" : isLogin ? "AUTHORIZE_SESSION" : "PROVISION_IDENTITY"}
              </button>

              {isForgot && (
                <p
                  onClick={() => setIsForgot(false)}
                  style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: 'var(--admin-text-dim)', cursor: 'pointer', fontWeight: 600, textTransform: 'uppercase' }}
                >
                  ABORT_RECOVERY: Back to Login
                </p>
              )}
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AuthPage;
